// components/contactPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MessageSquare, Send, CheckCircle, AlertCircle, Home } from 'lucide-react';
import { submitContactToAPI } from '../utils/api';

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Basic validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setError('Please fill in all required fields.');
      setIsLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address.');
      setIsLoading(false);
      return;
    }

    // Phone validation (if provided)
    if (formData.phone && !/^[\+]?[\d\s\-\(\)]{8,}$/.test(formData.phone.replace(/\s/g, ''))) {
      setError('Please enter a valid phone number.');
      setIsLoading(false);
      return;
    }

    const result = await submitContactToAPI(formData);
    setIsLoading(false);

    if (result.success) {
      setIsSubmitted(true);
      // Track contact form submission
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'contact_form_submitted', {
          'contact_subject': formData.subject
        });
      }
    } else {
      if (result.error.includes('reCAPTCHA')) {
        setError('Security verification failed. Please try again.');
      } else {
        setError('Failed to send message. Please try again.');
      }
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-gradient-to-br from-green-50 to-blue-50">
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Message Sent Successfully!</h1>
            <p className="text-lg text-gray-600 mb-6">
              Thank you for contacting CPR Refresh. We've received your message and will get back to you within 24-48 hours.
            </p>
            <p className="text-gray-600 mb-8">
              <strong>What's next?</strong> We'll review your message and respond via email. For urgent matters, 
              you can also use our contact form again if needed.
            </p>
            <div className="space-y-4">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                <Home className="mr-2 h-4 w-4" />
                Back to Home
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-100">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
            <Mail className="h-8 w-8 text-indigo-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Contact Us</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Have a question, suggestion, or need help? We'd love to hear from you! 
            Send us a message and we'll get back to you as soon as possible.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Get in Touch</h3>
              
              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-indigo-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-gray-900">Contact Form</h4>
                  <p className="text-gray-600">Use our contact form for all inquiries</p>
                  <p className="text-sm text-gray-500 mt-1">We respond within 24-48 hours</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <MessageSquare className="h-5 w-5 text-indigo-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-gray-900">Common Questions</h4>
                  <p className="text-gray-600">Check our FAQ page for quick answers</p>
                  <button
                    onClick={() => navigate('/faq')}
                    className="text-indigo-600 hover:text-indigo-700 text-sm mt-1"
                  >
                    Visit FAQ →
                  </button>
                </div>
              </div>

              <div className="border-t pt-6">
                <h4 className="font-medium text-gray-900 mb-3">What can we help with?</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Quiz questions or content</li>
                  <li>• Technical support</li>
                  <li>• Subscription management</li>
                  <li>• Feature suggestions</li>
                  <li>• Partnership inquiries</li>
                  <li>• General feedback</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
                      placeholder="Your full name"
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
                      placeholder="your.email@example.com"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
                    required
                    disabled={isLoading}
                  >
                    <option value="">Select a subject</option>
                    <option value="Question about CPR">Question about CPR</option>
                    <option value="Technical Support">Technical Support</option>
                    <option value="Quiz Content">Quiz Content</option>
                    <option value="Subscription Management">Subscription Management</option>
                    <option value="Feature Request">Feature Request</option>
                    <option value="Partnership Inquiry">Partnership Inquiry</option>
                    <option value="General Feedback">General Feedback</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors resize-vertical"
                    placeholder="Please provide details about your question or how we can help you..."
                    required
                    disabled={isLoading}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Minimum 10 characters. Be as specific as possible to help us assist you better.
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    This site is protected by reCAPTCHA and the Google{' '}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700">
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700">
                      Terms of Service
                    </a>{' '}
                    apply.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-4 px-6 rounded-lg font-medium transition-all duration-200 ${
                    isLoading
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transform hover:scale-[1.02]'
                  }`}
                >
                  {isLoading ? (
                    <span className="inline-flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending Message...
                    </span>
                  ) : (
                    <span className="inline-flex items-center">
                      <Send className="mr-2 h-5 w-5" />
                      Send Message
                    </span>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactPage;